import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Space, Spin, Table } from "antd";
import axios from "axios";
import { BASE_URL } from "../../../utils/constants";
import { Endpoint, User } from "../../../utils/interfaces";
import { NotificationInstance } from "antd/es/notification/interface";

interface Props {
  showModal: boolean;
  onClose: () => void;
  user: User | null;
  api: NotificationInstance;
}

// Always reachable with no credentials at all (see app.py's
// EXCEPTED_ENDPOINTS) - listing them here would just be noise, since
// assigning them to a service account has no effect.
const EXCEPTED_ENDPOINTS = new Set([
  "/", "/login", "/logout", "/getAToken", "/get-login-url",
  "/<path:filename>", "/install/<sn>", "/me", "/get_comments",
]);

const ManageEndpointsModal: React.FC<Props> = ({ showModal, onClose, user, api }) => {
  const [allEndpoints, setAllEndpoints] = useState<Endpoint[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [initialSelectedKeys, setInitialSelectedKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!showModal || !user) return;
    setSelectedKeys(user.allowed_endpoints ?? []);
    setInitialSelectedKeys(user.allowed_endpoints ?? []);
    setSearchText("");
    setShowOnlySelected(false);
    setLoading(true);
    axios
      .get(`${BASE_URL}get_url_map`)
      .then((response) => {
        // Two @route() declarations sharing the same path (e.g. one POST,
        // one GET) produce two separate rows here with an identical
        // `endpoint` string - the backend's auth check only ever looks at
        // that path, never the method, so selecting the endpoint grants all
        // of them. Merge those rows into one instead of just keeping the
        // first, so the Methods/Location columns reflect everything a
        // single checkbox actually covers.
        const merged = new Map<string, Endpoint>();
        for (const entry of response.data as Endpoint[]) {
          if (EXCEPTED_ENDPOINTS.has(entry.endpoint)) continue;
          const existing = merged.get(entry.endpoint);
          if (!existing) {
            merged.set(entry.endpoint, { ...entry });
            continue;
          }
          const methodSet = new Set([
            ...existing.methods.split(","),
            ...entry.methods.split(","),
          ]);
          existing.methods = Array.from(methodSet).sort().join(",");
          const locations = existing.location.split(" / ");
          if (!locations.includes(entry.location)) {
            existing.location = [...locations, entry.location].join(" / ");
          }
        }
        const deduped = Array.from(merged.values()).sort((a, b) => a.endpoint.localeCompare(b.endpoint));
        setAllEndpoints(deduped);
      })
      .catch((error) => {
        api.error({
          title: "Error Fetching Endpoints",
          description: `Failed to fetch the endpoint list: ${error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .finally(() => setLoading(false));
  }, [showModal, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty = useMemo(() => {
    if (selectedKeys.length !== initialSelectedKeys.length) return true;
    const initialSet = new Set(initialSelectedKeys);
    return selectedKeys.some((k) => !initialSet.has(k));
  }, [selectedKeys, initialSelectedKeys]);

  const filteredEndpoints = allEndpoints.filter((entry) => {
    if (showOnlySelected && !selectedKeys.includes(entry.endpoint)) return false;
    const haystack = `${entry.endpoint} ${entry.location} ${entry.methods}`.toLowerCase();
    return haystack.includes(searchText.toLowerCase());
  });

  const handleSave = () => {
    if (!user) return;
    setSaving(true);
    axios
      .post(`${BASE_URL}update_user/${user.id}`, { allowed_endpoints: selectedKeys })
      .then(() => {
        onClose();
        api.success({
          title: "Endpoints Updated",
          description: `Allowed endpoints for "${user.user_email}" were updated successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        api.error({
          title: "Error Updating Endpoints",
          description: `Failed to update endpoints for "${user.user_email}": ${error.response?.data?.reason ?? error.message}`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .finally(() => setSaving(false));
  };

  const columns = [
    {
      title: "Endpoint",
      dataIndex: "endpoint",
      key: "endpoint",
      width: 320,
      render: (v: string) => <code style={{ wordBreak: "break-all" }}>{v}</code>,
    },
    { title: "Methods", dataIndex: "methods", key: "methods", width: 160 },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (v: string) => <span style={{ wordBreak: "break-all" }}>{v}</span>,
    },
  ];

  return (
    <Modal
      title={`Manage Endpoints for ${user?.user_email ?? ""}`}
      open={showModal}
      onCancel={onClose}
      width={720}
      closable={!saving}
      mask={{ closable: !saving }}
      footer={[
        <Button key="close" onClick={onClose} disabled={saving}>Close</Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!isDirty || saving}
          onClick={handleSave}
        >Save Changes</Button>,
      ]}
    >
      <p>{selectedKeys.length} endpoint(s) selected. This service account will be denied everything else.</p>
      <Space.Compact style={{ display: "flex", marginBottom: 12 }}>
        <Input.Search
          placeholder="Search by endpoint, method, or blueprint"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Button
          type={showOnlySelected ? "primary" : "default"}
          onClick={() => setShowOnlySelected(!showOnlySelected)}
        >
          Show only selected
        </Button>
      </Space.Compact>
      <Spin size="large" spinning={loading}>
        <Table
          size="small"
          tableLayout="fixed"
          columns={columns}
          dataSource={filteredEndpoints}
          rowKey="endpoint"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: (keys) => setSelectedKeys(keys as string[]),
            preserveSelectedRowKeys: true,
          }}
        />
      </Spin>
    </Modal>
  );
};

export default ManageEndpointsModal;
